import { Router } from '@angular/router';
import { AppInitService } from './../services/app-init.service';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  mergeMap,
  tap,
  finalize,
  map,
  take,
} from 'rxjs/operators';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { AuthenticationService } from '../services/authentication.service';

//Dialog
import { MatDialog } from '@angular/material/dialog';
import { LoaderService } from '../services/loader.service';
import { EncryptionService } from '../services/encryption.service';
import { MessageDisplayService } from '../services/message-display/message-display.service';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import {
  authCodeFlowConfig,
  customOauth2Config,
} from 'src/assets/config/oauth2-config';
import { SessionCheckerService } from '../services/session-checker.service';
import {
  AuthType,
  EncryptedPayLoadKey,
  EncryptedParamKey,
  PayloadType,
  PayloadHeaders,
} from '../constants/digigov.constants';

@Injectable()
export class HttpsInterceptor implements HttpInterceptor {
  private _sessionCheckSkipApis = [
    '/login',
    this.appInitService.sessionCheckerIdUrl,
    '/dms/file-upload',
    '/report/export-report-with-charts',
  ];
  private _a = ['/login'];
  constructor(
    public dialog: MatDialog,
    private authenticationService: AuthenticationService,
    private loaderService: LoaderService,
    private encryptionService: EncryptionService,
    private appInitService: AppInitService,
    private messageDisplay: MessageDisplayService,
    private router: Router,

    //OIDC
    private oauthStorage: OAuthStorage,
    private oauthService: OAuthService,

    //Session Checker
    private sessionCheckerService: SessionCheckerService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // add authorization header with jwt token if available
    switch (this.appInitService?.appConfig?.authType) {
      case AuthType.JWT:
        const currentUser = this.authenticationService.currentUserValue;
        if (currentUser && currentUser.token) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${String(currentUser.token)}`,
            },
          });
        }
        return this.getHttpCall(request, next);
      case AuthType.COOKIE:
        //for Session Checker
        if (
          this.appInitService?.appConfig?.showSessionChecker &&
          !request.url.includes('/logout') &&
          localStorage.getItem('userAuthToken')
        ) {
          if (
            localStorage.getItem('param_1') &&
            new Date(Number(localStorage.getItem('param_1'))) < new Date()
          ) {
            this.authenticationService.logout(true);
          } else {
            this.sessionCheckerService.refreshCountDown(
              localStorage.getItem('param_1') &&
                new Date(Number(localStorage.getItem('param_1'))) < new Date()
            );
          }
        }
        request = request.clone({ withCredentials: true });
        if (request.params.get('tokenRequired')) {
          request.params.delete('tokenRequired');
          return this.authenticationService.getTokenFromCookie().pipe(
            mergeMap((token) => {
              request = request.clone({
                setHeaders: { Authorization: `Bearer ${token}` },
              });
              return this.getHttpCall(request, next);
            })
          );
        }
        return this.getHttpCall(request, next);
      case AuthType.OATUH2:
        let token = this.oauthStorage.getItem('access_token');
        let issuer = '';
        issuer = authCodeFlowConfig?.issuer;
        const index = issuer?.lastIndexOf('/');
        issuer = issuer?.substring(0, index);
        //for Session Checker
        if (
          this.appInitService?.appConfig?.showSessionChecker &&
          !request.url.includes('/logout') &&
          this.oauthStorage.getItem('access_token')
        ) {
          this.sessionCheckerService.refreshCountDown();
        }
        if (
          customOauth2Config?.refreshTokenEnabled &&
          customOauth2Config?.refreshTokenType.toLocaleLowerCase() ===
            'custom' &&
          this.appInitService.hasOauth2TokenExpired &&
          token &&
          (!issuer || !(this.normliseApiUrl(request.url)?.includes(this.normliseApiUrl(issuer))))
        ) {
          if (!this.appInitService.oauth2TokenCall) {
            this.appInitService.oauth2TokenCall = true;
            this.oauthService.refreshToken().then(() => {
              this.appInitService.oauth2TokenCall = false;
              token = this.oauthStorage.getItem('access_token');
              this.appInitService.oauth2AccessTokenReceived.next(token);
            });
          }
          return this.appInitService.oauth2AccessTokenReceived
            .asObservable()
            .pipe(
              take(1),
              mergeMap((token) => {
                request = request.clone({ withCredentials: true });
                request = request.clone({ withCredentials: true });
                if (request.params.get('tokenRequired')) {
                  request.params.delete('tokenRequired');
                }
                request = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${token}`,
                  },
                });
                return this.getHttpCall(request, next);
              })
            );
        } else {
          if (!issuer || !(this.normliseApiUrl(request.url)?.includes(this.normliseApiUrl(issuer)))) {
            request = request.clone({ withCredentials: true });
          }
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });
          return this.getHttpCall(request, next);
        }
      default:
        return this.getHttpCall(request, next);
    }
  }
  p2apis = ['/admin/my-profile', '/admin/user-login-dtls', '/user-profiles'];

  getHttpCall(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    //removed tokenRequired for jwt,oauth2
    request.params.delete('tokenRequired');

    let issuer = '';
    if (this.appInitService?.appConfig?.authType == AuthType.OATUH2) {
      issuer = authCodeFlowConfig?.issuer;
      const index = issuer?.lastIndexOf('/');
      issuer = issuer?.substring(0, index);
    }
    if (!issuer || !(this.normliseApiUrl(request.url)?.includes(this.normliseApiUrl(issuer)))) {
      //FOR CSP
      request = request.clone({
        headers: request.headers.set(
          'Content-Security-Policy',
          `default-src 'self'; connect-src *; font-src *; frame-src *; img-src * data:; media-src *; object-src *; script-src * 'unsafe-inline' 'unsafe-eval'; style-src * 'unsafe-inline';upgrade-insecure-requests;`
        ),
      });
      request = request.clone({
        headers: request.headers.set('Referrer-Policy', 'same-origin'),
      });
      if (this.authenticationService?.csrfToken) {
        request = request.clone({
          headers: request.headers.set(
            'X-CSRF-TOKEN',
            this.authenticationService?.csrfToken
          ),
        });
      }
    }

    let hasSessionSkipUrl = !!request.url.match(
      this._sessionCheckSkipApis.join('|')
    );
    if (
      this.appInitService?.appConfig?.authType == AuthType.OATUH2 &&
      issuer &&
      (this.normliseApiUrl(request.url)?.includes(this.normliseApiUrl(issuer)))
    ) {
      hasSessionSkipUrl = true;
    }
    //FORCE CHECK
    const _fc = !!request.url.match(this._a.join('|'));
    let sessionCheckObs = of(null);
    let addReqInList = true;
    let id = ''; //this.encryptionService.generateSalt();
    // if (_fc) {
    //   id = request.headers.get('_fc');
    //   request = request.clone({
    //     headers: request.headers.delete('_fc'),
    //   });
    //   request = this.payloadConverter(request, id, _fc);
    // }
    request = this.addMicroServicePathInUrl(request);
    if (_fc) {
      //QUICK FIX FOR DUPLICTATE URL
      if (request.headers.get('_fc')) {
        id = request.headers.get('_fc');
        request = request.clone({
          headers: request.headers.delete('_fc'),
        });
        request = this.payloadConverter(request, id, _fc);
      }
    } else if (
      ((request?.body && !request?.body?.[EncryptedPayLoadKey]) ||
        ((request.params.keys().length || request.url.includes('?')) &&
          !request.params.get(EncryptedParamKey))) &&
      !hasSessionSkipUrl
    ) {
      switch (this.appInitService.appConfig.payload) {
        case PayloadType.CLASSIC:
          //encrypt payload with default salt key
          id = this.encryptionService.param3;
          break;
        case PayloadType.MODERN:
          //generating salt from UI and Passing
          id = this.encryptionService.generateSalt();
          break;
        case PayloadType.FUTURISTIC:
          //get salt from backend which is encrypted need to decrypt with default salt key
          id = '';
          sessionCheckObs = this.appInitService.getSessionCheckerId();
          addReqInList = false;
          break;
      }
      if (id) {
        request = this.payloadConverter(request, id, _fc);
      }
    }

    this.loaderService.isLoading.next(true);
    request = request.clone({
      url: this.getEndPoint(request.url, request),
    });
    if (addReqInList) {
      this.loaderService.addRequest(request);
    }

    return sessionCheckObs.pipe(
      mergeMap((id) => {
        if (id) {
          request = this.payloadConverter(request, id, _fc);
          this.loaderService.addRequest(request);
        }
        return next.handle(request).pipe(
          debounceTime(500),
          map((res) => {
            this.p2apis.forEach((api) => {
              if (
                request.url.includes(api) &&
                this.appInitService.appConfig.P2_DATA_ENC_ENABLE
              ) {
                const temp = this.encryptionService.decrypt(
                  res?.['body']?.data,
                  this.encryptionService.param3
                );
                res['body'].data = JSON.parse(temp);
              }
            });
            return res;
          }),
          tap((event: HttpEvent<any>) => {
            if (
              event instanceof HttpResponse &&
              event.url.includes('/common/address-details/addressCode') &&
              (event.status === 200 || event.status === 201)
            ) {
              let customResponse = event.body;
              customResponse['data'].addressFieldVOList.forEach((element) => {
                if (element.textValue && element.textValue.includes('=')) {
                  const textvalue = this.encryptionService.decrypt(
                    element.textValue,
                    this.encryptionService?.param3
                  );
                  element.textValue = textvalue;
                }
                return event.clone({
                  body: customResponse,
                });
              });
            }
            if (
              event instanceof HttpResponse &&
              (event.status === 200 || event.status === 201)
            ) {
              this.messageDisplay.successMsg(event);
            } else if (event instanceof HttpResponse && event.status === 302) {
              //Check for 302
              this.router.navigateByUrl(event.headers.get('Location'));
            }
          }),
          catchError((response: HttpErrorResponse) => {
            const err = this.messageDisplay.errorMessage(response);
            return throwError(err);
          }),
          finalize(() => {
            this.loaderService.removeRequest(request);
          })
        );
      })
    );
  }

  getEndPoint = (url: string, request) => {
    // let url = request.urlWithParams;
    if (!url) throw new Error('Invalid url');
    if (url?.trim()?.charAt(0) === '/') {
      if (!this.appInitService?.appConfig?.baseUrl)
        throw new Error('Server URL not initialized');
      let contextUrl = '';
      let microServiceCode = '';
      const reqWithParams = request.urlWithParams;
      const queryContext = this.router.parseUrl(reqWithParams).queryParams;
      request?.params?.map?.delete('microServiceCode');
      request?.params?.map?.delete('appCode');
      url = request.url;
      if (queryContext) {
        const microDb = localStorage.getItem('micro_services')
          ? JSON.parse(localStorage.getItem('micro_services'))
          : null;
        const contextDb = localStorage.getItem('contextPath')
          ? JSON.parse(localStorage.getItem('contextPath'))
          : null;
        if (microDb && contextDb && queryContext['appCode']) {
          microDb?.forEach((element: any) => {
            if (element['appCode'] === queryContext['appCode']) {
              microServiceCode = element.microServiceCode;
            }
          });
          contextDb?.forEach((element: any) => {
            if (element['microServiceCode'] === microServiceCode) {
              contextUrl = `${this.appInitService?.appConfig?.baseUrl}${element['contextPath']}${url}`;
            }
          });
        }
        if (contextDb && queryContext['microServiceCode']) {
          contextDb?.forEach((element: any) => {
            if (
              element['microServiceCode'] === queryContext['microServiceCode']
            ) {
              contextUrl = `${this.appInitService?.appConfig?.baseUrl}${element['contextPath']}${url}`;
            }
          });
        }
      }
      if (contextUrl) {
        return contextUrl;
      } else {
        return `${this.appInitService?.appConfig?.baseUrl}${url}`;
      }
    }
    else if (url?.trim()?.charAt(0) === '+') {
      url = url.trim().substr(1);
      console.log(`${this.appInitService?.appConfig?.baseGlobalMater}${url}`)
      return `${this.appInitService?.appConfig?.baseGlobalMater}${url}`;
    } else if (url?.trim()?.charAt(0) === '^') {
      url = url.trim().substr(1);
      return `${this.appInitService?.appConfig?.basePayeeUrl}${url}`;
    } else if (url?.trim()?.charAt(0) === '*') {
      url = url.trim().substr(1);
      return `${this.appInitService?.appConfig?.baseDepositUrl}${url}`;
    } else if (url?.trim()?.charAt(0) === '~') {
      url = url.trim().substr(1);
      return `${this.appInitService?.appConfig?.baseCommonUrl}${url}`;
    }
   
    return url;
  };
  private payloadConverter(
    request: HttpRequest<any>,
    id: string,
    fc = false
  ): HttpRequest<any> {
    if (!fc) {
      if (this.appInitService.appConfig.payload != PayloadType.LEGACY) {
        let body = {};
        body[EncryptedPayLoadKey] = this.encryptionService.encrypt(
          JSON.stringify(request.body),
          id
        ).cipherText;
        request = request.clone({
          body: body,
        });
      }
    }
    const paramsList = request.params.keys();
    let paramArr: string[] = [];
    //QUICK FIX FOR PARAMS IN URL
    if (
      request.url.includes('?') &&
      this.appInitService.appConfig.payload != PayloadType.LEGACY &&
      !paramsList.includes(EncryptedParamKey)
    ) {
      const paramStringArr = request.url.substring(
        request.url.indexOf('?') + 1
      );
      const pArr = paramStringArr.split('&');
      paramArr = paramArr.concat(pArr);
      request = request.clone({
        url: request.url.substring(0, request.url.indexOf('?')),
      });
    }
    if (
      (paramArr.length || paramsList.length) &&
      this.appInitService.appConfig.payload != PayloadType.LEGACY &&
      !paramsList.includes(EncryptedParamKey)
    ) {
      paramsList.forEach((param) => {
        const paramString = `${param}=${request.params.get(param)}`;
        paramArr.push(paramString);
        request = request.clone({
          params: request.params.delete(param),
        });
      });
      const ps = paramArr.join('&');
      const p = encodeURIComponent(
        this.encryptionService.encrypt(ps, id).cipherText
      );
      request = request.clone({
        params: request.params.set(EncryptedParamKey, p),
      });
    }
    let p1 = this.appInitService.generateSessionClientId(
      1,
      this.appInitService.clientId()
    );
    let p2 = this.appInitService.generateSessionClientId(
      2,
      this.appInitService.clientId()
    );
    let p3 = this.appInitService.generateSessionClientId(
      3,
      this.appInitService.clientId()
    );
    let p4 = this.appInitService.generateSessionClientId(
      4,
      this.appInitService.clientId()
    );

    request = request.clone({
      headers: request.headers.set(PayloadHeaders.PARAM1, p1),
    });
    p2 = p1 + p2 + p4;
    request = request.clone({
      headers: request.headers.set(
        PayloadHeaders.PARAM2,
        this.encryptionService.encrypt(p2, this.encryptionService.param3)
          .cipherText
      ),
    });
    p3 = p1 + id + p4;
    request = request.clone({
      headers: request.headers.set(
        PayloadHeaders.PARAM3,
        this.encryptionService.encrypt(p3, this.encryptionService.param3)
          .cipherText
      ),
    });
    request = request.clone({
      headers: request.headers.set(PayloadHeaders.PARAM4, p4),
    });

    return request;
  }
  addMicroServicePathInUrl(request: HttpRequest<any>): HttpRequest<any> {
    let microServiceCode = '';
    const reqWithParams = request.urlWithParams;
    const queryContext = this.router.parseUrl(reqWithParams).queryParams;
    request = request.clone({
      params: request.params.delete('microServiceCode'),
    });
    request = request.clone({
      params: request.params.delete('appCode'),
    });

    if (queryContext) {
      const microDb = localStorage.getItem('micro_services')
        ? JSON.parse(localStorage.getItem('micro_services'))
        : null;
      const contextDb = localStorage.getItem('contextPath')
        ? JSON.parse(localStorage.getItem('contextPath'))
        : null;
      if (microDb && contextDb && queryContext['appCode']) {
        microDb?.forEach((element: any) => {
          if (element['appCode'] === queryContext['appCode']) {
            microServiceCode = element.microServiceCode;
          }
        });
        contextDb?.forEach((element: any) => {
          if (element['microServiceCode'] === microServiceCode) {
            request = request.clone({
              url: `${element['contextPath']}${request.url}`,
            });
          }
        });
      }
      if (contextDb && queryContext['microServiceCode']) {
        contextDb?.forEach((element: any) => {
          if (
            element['microServiceCode'] === queryContext['microServiceCode']
          ) {
            request = request.clone({
              url: `${element['contextPath']}${request.url}`,
            });
          }
        });
      }
    }
    return request;
  }

  normliseApiUrl(url:string){
  
    try {
      const normalUrl=new URL(url);
      if(normalUrl.protocol==='https:' && normalUrl.port==='443' || normalUrl.protocol==='http:' && normalUrl.port==='80' ){
        normalUrl.port='';
      }
      return normalUrl.toString();
    }
    catch(e){
      return null;
    }
  }
}
